// UserActivityLogs.styles.js
import styled from 'styled-components';

const TEAL = '#61cbc9';
const TEAL_HOVER = 'rgba(97,203,201,0.9)';
const TEAL_LIGHT = 'rgba(97,203,201,0.15)';

// ── Page wrapper ──────────────────────────────────────────────────────────────
export const Container = styled.div`
  padding: 20px 28px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  font-family: "Roboto", sans-serif;
  color: #333;
  box-sizing: border-box;
`;

// ── Filter bar ────────────────────────────────────────────────────────────────
export const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 10px;
  padding: 16px 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FilterLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const FilterInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 14px;
  width: 140px;
  font-family: "Roboto", sans-serif;
  transition: all 0.2s;
  &:focus {
    border-color: ${TEAL};
    outline: none;
    box-shadow: 0 0 4px rgba(97,203,201,0.4);
  }
`;

export const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 14px;
  width: 180px;
  font-family: "Roboto", sans-serif;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
  &:focus {
    border-color: ${TEAL};
    outline: none;
    box-shadow: 0 0 4px rgba(97,203,201,0.4);
  }
  &:hover { border-color: ${TEAL}; }
`;

export const DateInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 13px;
  font-family: "Roboto", sans-serif;
  transition: all 0.2s;
  &:focus {
    border-color: ${TEAL};
    outline: none;
    box-shadow: 0 0 4px rgba(97,203,201,0.4);
  }
`;

export const FilterActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
  align-self: flex-end;
`;

export const SearchButton = styled.button`
  background-color: ${TEAL};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  font-family: "Roboto", sans-serif;
  transition: background-color 0.2s;
  &:hover { background-color: ${TEAL_HOVER}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const ResetButton = styled.button`
  background-color: transparent;
  color: #666;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 14px;
  font-family: "Roboto", sans-serif;
  transition: all 0.2s;
  &:hover { border-color: #999; color: #333; background: #f5f5f5; }
`;

// ── Summary ───────────────────────────────────────────────────────────────────
export const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  color: #777;
  padding: 0 2px;
`;

// ── Table ─────────────────────────────────────────────────────────────────────
export const TableWrapper = styled.div`
  width: 100%;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: separate;
  border-spacing: 0;
`;

export const TableHead = styled.thead`
  position: sticky;
  top: 0;
  background-color: #f9fafb;
  border-bottom: 2px solid #eaeaea;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
`;

export const TableHeader = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  color: #444;
  white-space: nowrap;
  border-bottom: 2px solid #eaeaea;
  font-size: 13px;
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr`
  font-size: 14px;
  transition: background 0.2s, box-shadow 0.2s;
  &:hover {
    background-color: #f7fdfc;
    box-shadow: 0px 1px 3px rgba(0,0,0,0.05);
  }
`;

export const TableData = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #f1f1f1;
  vertical-align: middle;
`;

// ── Badges & chips ────────────────────────────────────────────────────────────
export const EventBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background-color: ${({ type }) => {
        const t = (type || '').toLowerCase();
        if (t.includes('create') || t.includes('add')) return '#DCFCE7';
        if (t.includes('update') || t.includes('edit')) return '#DBEAFE';
        if (t.includes('delete') || t.includes('remove')) return '#FEE2E2';
        if (t.includes('password')) return '#EDE9FE';
        if (t.includes('login') || t.includes('logout')) return '#FEF9C3';
        return '#F3F4F6';
    }};
  color: ${({ type }) => {
        const t = (type || '').toLowerCase();
        if (t.includes('create') || t.includes('add')) return '#166534';
        if (t.includes('update') || t.includes('edit')) return '#1E40AF';
        if (t.includes('delete') || t.includes('remove')) return '#991B1B';
        if (t.includes('password')) return '#5B21B6';
        if (t.includes('login') || t.includes('logout')) return '#854D0E';
        return '#374151';
    }};
`;

export const EntityChip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${TEAL_LIGHT};
  color: #1a6b69;
  white-space: nowrap;
`;

export const DetailBox = styled.div`
  background-color: ${({ accent }) => accent || '#F3F4F6'};
  border-radius: 6px;
  padding: 3px 10px;
  display: inline-block;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  cursor: default;
`;

export const DetailLine = styled.div`
  font-size: ${({ first }) => first ? '12px' : '11px'};
  font-weight: ${({ first }) => first ? '600' : '400'};
  color: ${({ first }) => first ? '#111827' : '#4B5563'};
  line-height: 1.6;
  word-break: break-word;
`;

// Tooltip wrapper — position: relative on the td
export const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: default;

  &:hover > .detail-tooltip {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
`;

export const DetailTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  z-index: 100;
  background: ${({ accent }) => accent || '#F3F4F6'};
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 8px;
  padding: 8px 12px;
  min-width: 180px;
  max-width: 280px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  opacity: 0;
  pointer-events: none;
  transform: translateY(4px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  white-space: normal;
`;

// ── Avatar ────────────────────────────────────────────────────────────────────
export const AvatarCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${TEAL_LIGHT};
  color: #1a6b69;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-transform: uppercase;
`;

export const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const UserName = styled.div`
  font-weight: 500;
  color: #1a1d23;
  font-size: 13px;
`;

export const UserId = styled.div`
  font-size: 11px;
  color: #9ca3af;
`;

export const TimeCell = styled.span`
  color: #6b7280;
  white-space: nowrap;
  font-size: 12px;
`;

// ── State boxes ───────────────────────────────────────────────────────────────
export const StateBox = styled.div`
  text-align: center;
  padding: 60px 24px;
  color: #9ca3af;
`;

export const StateIcon = styled.div`
  font-size: 36px;
  margin-bottom: 12px;
`;

export const StateText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
`;

export const StateSubText = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

// ── Pagination ────────────────────────────────────────────────────────────────
export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 6px;
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 10px;
`;

export const RowsSelect = styled.select`
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 14px;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: ${TEAL}; }
`;

export const PageSelect = styled.select`
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 14px;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: ${TEAL}; }
`;

export const PageButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  background: ${({ active }) => active ? TEAL : 'transparent'};
  border: 1px solid ${({ active }) => active ? TEAL : '#ccc'};
  color: ${({ active }) => active ? '#fff' : '#333'};
  cursor: pointer;
  min-width: 36px;
  font-size: 14px;
  transition: all 0.2s ease;
  &:hover { background-color: ${TEAL_LIGHT}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ── Error popup ───────────────────────────────────────────────────────────────
export const ErrorBanner = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  color: #991B1B;
  font-size: 13px;
`;

// ── Spinner ───────────────────────────────────────────────────────────────────
export const SpinnerRing = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #E5E7EB;
  border-top: 3px solid ${TEAL};
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin: 0 auto 12px;
  @keyframes spin { to { transform: rotate(360deg); } }
`;